import type { RightPanelKind } from "../rightPanelSurface";
import type { CloudRuntimeRouteBlockState } from "./ensureActiveRouteCloudRuntime";
import type { BrowserSessionSnapshot } from "@cmmd-forge/contracts";
import { selectPreferredPreviewTab } from "../components/BrowserPreviewPanel.logic";
import { deriveBrowserSidebarLabel } from "../components/ThreadRightPanelRouteLayout";

export interface DiffPanelMountState {
  readonly threadKey: string | null;
  readonly hasOpenedDiff: boolean;
}

export interface CloudThreadRoutePresentation {
  readonly showHistory: true;
  readonly showRecoveryBanner: false;
}

/**
 * Runtime admission is a send-time concern. A route may need wake, repair, or
 * update, but that state must never replace the durable conversation surface.
 */
export function resolveCloudThreadRoutePresentation(
  _cloudRouteBlockState: CloudRuntimeRouteBlockState | null,
): CloudThreadRoutePresentation {
  return {
    showHistory: true,
    // Runtime repair, wake, and update are initiated by the first send. The
    // route stays conversational even when admission is currently blocked.
    showRecoveryBanner: false,
  };
}

export function resolveHasOpenedDiffForThread(input: {
  readonly currentThreadKey: string | null;
  readonly diffOpen: boolean;
  readonly state: DiffPanelMountState;
}): boolean {
  return input.state.threadKey === input.currentThreadKey
    ? input.state.hasOpenedDiff
    : input.diffOpen;
}

export function markDiffPanelOpenedForThread(input: {
  readonly currentThreadKey: string | null;
  readonly previous: DiffPanelMountState;
}): DiffPanelMountState {
  if (input.previous.threadKey === input.currentThreadKey && input.previous.hasOpenedDiff) {
    return input.previous;
  }
  return {
    threadKey: input.currentThreadKey,
    hasOpenedDiff: true,
  };
}

export function shouldRenderDiffPanelContent(input: {
  readonly rightPanel: RightPanelKind | null;
  readonly hasOpenedDiff: boolean;
}): boolean {
  return input.rightPanel === "diff" || input.hasOpenedDiff;
}

export function shouldRenderRightPanelSurface(input: {
  readonly hasSidebarState: boolean;
  readonly sidebarOpen: boolean;
}): boolean {
  return input.hasSidebarState || input.sidebarOpen;
}

export function resolveBrowserPreviewSidebarLabel(input: {
  readonly browserThreadState:
    | {
        readonly selectedBrowserId: string | null;
        readonly session: BrowserSessionSnapshot | null;
        readonly sessionsByBrowserId: Readonly<Record<string, BrowserSessionSnapshot>>;
      }
    | null
    | undefined;
  readonly previewTabId: string | null | undefined;
}): string {
  const browserSession = input.browserThreadState?.session ?? null;
  const selectedBrowserId =
    input.browserThreadState?.selectedBrowserId ?? browserSession?.browserId;
  const selectedSession =
    (selectedBrowserId ? input.browserThreadState?.sessionsByBrowserId[selectedBrowserId] : null) ??
    browserSession;
  const selectedTab = selectedSession
    ? selectPreferredPreviewTab({
        tabs: selectedSession.tabs,
        routeTabId: input.previewTabId ?? null,
        activeTabId: selectedSession.activeTabId,
      })
    : null;
  return deriveBrowserSidebarLabel({
    title: selectedTab?.title ?? null,
    url: selectedTab?.url ?? null,
  });
}
